import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useConfig } from "@/contexts/ConfigContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { X, Plus } from "lucide-react";
import { toast } from "sonner";

export const SettingsAdmin = () => {
  const { t } = useLang();
  const cfg = useConfig();
  const [form, setForm] = useState(null);
  const [newLabel, setNewLabel] = useState("");

  useEffect(() => { if (cfg?.settings) setForm({ ...cfg.settings }); }, [cfg?.settings]);

  if (!form) return <div className="text-sm text-slate-400">Loading...</div>;

  const save = async () => {
    try {
      await api.put("/settings", {
        ...form,
        default_tax_percent: Number(form.default_tax_percent) || 0,
        company_email: form.company_email || null,
      });
      toast.success(t("settings_saved"));
      await cfg.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const addLabel = () => {
    const v = newLabel.trim();
    if (!v || form.expense_labels.includes(v)) return;
    setForm({ ...form, expense_labels: [...form.expense_labels, v] });
    setNewLabel("");
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="settings-admin">
      <Card className="p-5 border-slate-200 rounded-2xl space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">{t("company_name")}</Label>
            <Input data-testid="company-name-input" value={form.company_name}
              onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">{t("company_email")}</Label>
            <Input data-testid="company-email-input" value={form.company_email || ""}
              onChange={(e) => setForm({ ...form, company_email: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">{t("currency")}</Label>
            <Input disabled value="IDR (Rp)" data-testid="currency-input" />
          </div>
          <div>
            <Label className="text-xs">{t("default_tax")} %</Label>
            <Input type="number" min={0} max={100} data-testid="default-tax-input" value={form.default_tax_percent}
              onChange={(e) => setForm({ ...form, default_tax_percent: e.target.value })} />
          </div>
        </div>

        <div>
          <Label className="text-xs">{t("expense_labels")}</Label>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {form.expense_labels.map((l) => (
              <span key={l} data-testid={`expense-label-chip-${l}`}
                className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-600">
                {l}
                <button onClick={() => setForm({ ...form, expense_labels: form.expense_labels.filter((x) => x !== l) })}
                  className="hover:text-rose-600"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
          <div className="flex gap-2 mt-2">
            <Input className="h-9 text-xs" placeholder={t("expense_label")} data-testid="new-expense-label-input"
              value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addLabel()} />
            <Button variant="outline" className="h-9 gap-1" data-testid="add-expense-label-button" onClick={addLabel}>
              <Plus className="w-3.5 h-3.5" /> {t("add")}
            </Button>
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={save} data-testid="save-settings-button" className="bg-orange-500 hover:bg-orange-600">{t("save")}</Button>
        </div>
      </Card>
    </div>
  );
};

export default SettingsAdmin;
